/**
 * Subscriptions — plan changes, trial extension, quota reset, entitlement overrides
 */
import React, { useState, useCallback } from "react";
import { subscriptions as subApi } from "../api.js";
import { useAsync, useDebounce } from "../hooks.js";

function statusBadge(status: string) {
  const map: Record<string, string> = {
    active: "ops-badge-green", trialing: "ops-badge-purple",
    past_due: "ops-badge-amber", suspended: "ops-badge-red",
    cancelled: "ops-badge-neutral", paused: "ops-badge-neutral",
  };
  return <span className={`ops-badge ${map[status] ?? "ops-badge-neutral"}`}>{status}</span>;
}

type Modal =
  | { type: "plan"; tenantId: string; currentPlan: string }
  | { type: "trial"; tenantId: string }
  | { type: "quota"; tenantId: string }
  | { type: "grant"; tenantId: string }
  | { type: "detail"; tenantId: string };

export function SubscriptionsView() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [plan, setPlan]     = useState("");
  const [page, setPage]     = useState(1);
  const [modal, setModal]   = useState<Modal | null>(null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState<string | null>(null);

  // Modal form state
  const [newPlan, setNewPlan]         = useState("");
  const [trialDays, setTrialDays]     = useState("7");
  const [entType, setEntType]         = useState("feature_flag");
  const [entKey, setEntKey]           = useState("");
  const [entValue, setEntValue]       = useState("true");
  const [entReason, setEntReason]     = useState("");

  const fetcher = useCallback(
    () => subApi.list({ q: search, status, plan: plan ?? "", page: String(page), pageSize: "50" }),
    [search, status, plan, page]
  );
  const { data, loading, error, reload } = useAsync<any>(fetcher, [search, status, plan, page]);

  const detail = useAsync<any>(
    () => modal?.type === "detail" ? subApi.get(modal.tenantId) : Promise.resolve(null),
    [modal?.type === "detail" ? (modal as any).tenantId : null]
  );

  async function doAction() {
    if (!modal) return;
    setBusy(true); setErr(null);
    try {
      if (modal.type === "plan")  await subApi.changePlan(modal.tenantId, newPlan);
      if (modal.type === "trial") await subApi.extendTrial(modal.tenantId, parseInt(trialDays, 10));
      if (modal.type === "quota") await subApi.resetQuota(modal.tenantId);
      if (modal.type === "grant") await subApi.grantEntitlement(modal.tenantId, {
        type: entType, key: entKey, value: entValue, reason: entReason,
      });
      setModal(null); reload();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const rows: any[] = data?.items ?? [];

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <div>
          <h1 className="ops-page-title">Subscriptions</h1>
          {data && <p className="ops-page-subtitle">{data.total} total</p>}
        </div>
      </div>
      {error && <div className="ops-error">{error}</div>}

      <div className="ops-filters">
        <input className="ops-input" placeholder="Filter by tenant…"
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <select className="ops-select" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          {["active","trialing","past_due","suspended","cancelled","paused"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select className="ops-select" value={plan} onChange={(e) => { setPlan(e.target.value); setPage(1); }}>
          <option value="">All plans</option>
          {["starter","professional","commerce_lite","internal_demo"].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <button className="ops-btn ops-btn-secondary ops-btn-sm" onClick={reload}>↻</button>
      </div>

      {loading && <div className="ops-loading">Loading…</div>}
      {!loading && rows.length === 0 && <div className="ops-empty">No subscriptions found.</div>}

      {rows.length > 0 && (
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Slug</th><th>Business</th><th>Plan</th><th>Status</th>
                <th>Trial end</th><th>Period end</th><th>AI msgs</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.tenant_id ?? r.id}>
                  <td><code style={{ fontSize: "0.75rem" }}>{r.slug}</code></td>
                  <td>{r.business_name ?? "—"}</td>
                  <td><span className="ops-badge ops-badge-purple">{r.plan_key}</span></td>
                  <td>{statusBadge(r.status ?? "—")}</td>
                  <td style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>{r.trial_end?.slice(0,10) ?? "—"}</td>
                  <td style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>{r.current_period_end?.slice(0,10) ?? "—"}</td>
                  <td style={{ fontSize: "0.75rem" }}>{r.ai_messages_used_this_month ?? 0}</td>
                  <td>
                    <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                      <button className="ops-btn ops-btn-sm ops-btn-secondary"
                        onClick={() => setModal({ type: "detail", tenantId: r.tenant_id })}>View</button>
                      <button className="ops-btn ops-btn-sm ops-btn-secondary"
                        onClick={() => { setNewPlan(r.plan_key); setModal({ type: "plan", tenantId: r.tenant_id, currentPlan: r.plan_key }); }}>Plan</button>
                      <button className="ops-btn ops-btn-sm ops-btn-secondary"
                        onClick={() => setModal({ type: "trial", tenantId: r.tenant_id })}>Trial</button>
                      <button className="ops-btn ops-btn-sm ops-btn-secondary"
                        onClick={() => setModal({ type: "quota", tenantId: r.tenant_id })}>Reset</button>
                      <button className="ops-btn ops-btn-sm ops-btn-secondary"
                        onClick={() => setModal({ type: "grant", tenantId: r.tenant_id })}>Grant</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.hasMore && (
        <div className="ops-pagination">
          {page > 1 && <button className="ops-btn ops-btn-sm ops-btn-secondary" onClick={() => setPage(p => p - 1)}>← Prev</button>}
          <span>Page {page}</span>
          <button className="ops-btn ops-btn-sm ops-btn-secondary" onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* Modals */}
      {modal && modal.type !== "detail" && (
        <div className="ops-modal-overlay" onClick={() => setModal(null)}>
          <div className="ops-modal" onClick={e => e.stopPropagation()}>
            <h2 className="ops-modal-title">
              {modal.type === "plan"  && "Change Plan"}
              {modal.type === "trial" && "Extend Trial"}
              {modal.type === "quota" && "Reset Quota"}
              {modal.type === "grant" && "Grant Entitlement"}
            </h2>
            {modal.type === "plan" && (
              <div className="ops-form-group">
                <label className="ops-form-label">New plan</label>
                <select className="ops-select" style={{ width: "100%" }} value={newPlan} onChange={e => setNewPlan(e.target.value)}>
                  {["starter","professional","commerce_lite","internal_demo"].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}
            {modal.type === "trial" && (
              <div className="ops-form-group">
                <label className="ops-form-label">Additional days</label>
                <input className="ops-input" type="number" min="1" value={trialDays} onChange={e => setTrialDays(e.target.value)} />
              </div>
            )}
            {modal.type === "quota" && (
              <p style={{ color: "var(--ops-muted)", fontSize: "0.875rem", marginTop: 0 }}>
                This will reset all AI quota counters for the current billing period.
              </p>
            )}
            {modal.type === "grant" && (
              <>
                <div className="ops-form-group">
                  <label className="ops-form-label">Type</label>
                  <select className="ops-select" style={{ width: "100%" }} value={entType} onChange={e => setEntType(e.target.value)}>
                    <option value="feature_flag">feature_flag</option>
                    <option value="quota">quota</option>
                    <option value="module_access">module_access</option>
                  </select>
                </div>
                <div className="ops-form-group">
                  <label className="ops-form-label">Key</label>
                  <input className="ops-input" value={entKey} onChange={e => setEntKey(e.target.value)} placeholder="e.g. ai_starter_engine" />
                </div>
                <div className="ops-form-group">
                  <label className="ops-form-label">Value</label>
                  <input className="ops-input" value={entValue} onChange={e => setEntValue(e.target.value)} placeholder="true / false / 500" />
                </div>
                <div className="ops-form-group">
                  <label className="ops-form-label">Reason</label>
                  <input className="ops-input" value={entReason} onChange={e => setEntReason(e.target.value)} placeholder="Optional" />
                </div>
              </>
            )}
            {err && <div className="ops-error">{err}</div>}
            <div className="ops-modal-actions">
              <button className="ops-btn ops-btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="ops-btn ops-btn-primary" disabled={busy} onClick={doAction}>
                {busy ? "Working…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {modal?.type === "detail" && (
        <div className="ops-modal-overlay" onClick={() => setModal(null)}>
          <div className="ops-modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <h2 className="ops-modal-title">Subscription Detail</h2>
            {detail.loading && <div className="ops-loading">Loading…</div>}
            {detail.data && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", fontSize: "0.8125rem" }}>
                  {Object.entries(detail.data.usageSummary ?? {}).map(([k, v]: any) => (
                    <div key={k} className="ops-card" style={{ padding: "0.625rem 0.875rem" }}>
                      <div style={{ color: "var(--ops-muted)", fontSize: "0.7rem", marginBottom: "0.25rem" }}>
                        {k.replace(/_/g, " ")}
                      </div>
                      <div>
                        <strong>{v.used}</strong>
                        {v.limit !== "unlimited" ? ` / ${v.limit}` : " / ∞"}
                      </div>
                    </div>
                  ))}
                </div>
                {detail.data.overrides?.length > 0 && (
                  <div style={{ marginTop: "1rem" }}>
                    <p className="ops-card-title">Entitlement Overrides</p>
                    {detail.data.overrides.map((o: any, i: number) => (
                      <div key={i} style={{ fontSize: "0.75rem", color: "var(--ops-muted)", padding: "0.25rem 0" }}>
                        <code>{o.type}:{o.key}</code> = <strong>{o.value}</strong>
                        {o.reason && <span> — {o.reason}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <div className="ops-modal-actions">
              <button className="ops-btn ops-btn-secondary" onClick={() => setModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
